/**
 * FinanceExpensesPanel — expenses ledger + subscriber analytics + Excel/PDF export.
 * Embedded in Secure Vault → Financials (merged from the old /admin/finance page).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Download, Plus, Trash2, Upload, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { apiClient } from '../../api/client';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#7B4FD8', '#F5831F', '#10B981', '#EF4444', '#6366F1', '#EC4899', '#F59E0B', '#14B8A6', '#6B7280'];

export const FinanceExpensesPanel = () => {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), category: 'OpenAI', amount: '', currency: 'GBP', notes: '' });
  const [csvText, setCsvText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiClient.request('/api/admin/finance/expenses').then(setData).catch(() => setData({ expenses: [], categories: [] }));
    apiClient.request('/api/admin/finance/summary').then(setSummary).catch(() => setSummary(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addExpense = async () => {
    if (!form.amount) return;
    try {
      await apiClient.request('/api/admin/finance/expenses', { method: 'POST', body: JSON.stringify({ ...form, amount: parseFloat(form.amount) }) });
      setForm({ ...form, amount: '', notes: '' });
      toast.success('Expense added');
      load();
    } catch (e) { toast.error(e?.message || 'Failed'); }
  };

  const removeExpense = async (id) => {
    try { await apiClient.request(`/api/admin/finance/expenses/${id}`, { method: 'DELETE' }); load(); } catch (e) { toast.error('Delete failed'); }
  };

  const importCsv = async () => {
    if (!csvText.trim()) return;
    setBusy(true);
    try {
      const r = await apiClient.request('/api/admin/finance/expenses/import-csv', { method: 'POST', body: JSON.stringify({ csv_text: csvText }) });
      toast.success(`Imported ${r.imported} rows (${r.skipped} skipped)`);
      setCsvText('');
      load();
    } catch (e) { toast.error(e?.message || 'Import failed'); }
    finally { setBusy(false); }
  };

  const download = async (kind) => {
    setBusy(true);
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      const r = await fetch(`${API_URL}/api/admin/finance/export/${kind}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `hibff-finance.${kind === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const monthData = summary ? Object.entries(summary.expenses_by_month).map(([m, v]) => ({ month: m, total: v })) : [];
  const catData = summary ? Object.entries(summary.expenses_by_category).map(([name, value]) => ({ name, value })) : [];
  const tierData = summary ? Object.entries(summary.subscribers_by_tier).map(([name, value]) => ({ name, value })) : [];
  const signupData = summary ? Object.entries(summary.signups_by_month).map(([m, v]) => ({ month: m, signups: v })) : [];

  return (
    <div className="space-y-5 bg-white rounded-2xl border-2 border-gray-200 p-5" data-testid="finance-vault">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Expenses & Revenue Ledger</h3>
          <p className="text-xs text-gray-500">Expenses, subscribers and revenue — Monday's Investor Pulse includes this. Export as Excel / PDF.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => download('excel')} disabled={busy} data-testid="export-excel-btn"
            className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 disabled:opacity-50" style={{ backgroundColor: '#10B981' }}>
            <Download size={14} /> Excel
          </button>
          <button onClick={() => download('pdf')} disabled={busy} data-testid="export-pdf-btn"
            className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 disabled:opacity-50" style={{ backgroundColor: '#EF4444' }}>
            <Download size={14} /> PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[['Total expenses', summary ? `£${summary.total_expenses.toLocaleString()}` : '—'],
          ['Expense entries', summary?.expense_count ?? '—'],
          ['Active subscribers', summary ? Object.values(summary.subscribers_by_tier).reduce((a, b) => a + b, 0) : '—'],
          ['Signups (12 mo)', summary ? Object.values(summary.signups_by_month).reduce((a, b) => a + b, 0) : '—'],
        ].map(([label, val]) => (
          <div key={label} className="bg-white p-4 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-2xl font-black" style={{ color: '#1A1209' }}>{val}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <h3 className="text-sm font-bold mb-2">Expenses by Month</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthData}><XAxis dataKey="month" fontSize={10} /><YAxis fontSize={10} /><Tooltip /><Bar dataKey="total" fill="#7B4FD8" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white p-4 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <h3 className="text-sm font-bold mb-2">Expenses by Category</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={catData} dataKey="value" nameKey="name" outerRadius={80} label>{catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Legend /><Tooltip /></PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white p-4 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <h3 className="text-sm font-bold mb-2">Active Subscribers by Plan</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tierData}><XAxis dataKey="name" fontSize={10} /><YAxis fontSize={10} allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill="#F5831F" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white p-4 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <h3 className="text-sm font-bold mb-2">Signups by Month</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={signupData}><XAxis dataKey="month" fontSize={10} /><YAxis fontSize={10} allowDecimals={false} /><Tooltip /><Bar dataKey="signups" fill="#10B981" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
        <h3 className="text-sm font-bold mb-3">Add Expense</h3>
        <div className="flex flex-wrap gap-2">
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="expense-date" />
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="expense-category">
            {(data?.categories || []).map(c => <option key={c}>{c}</option>)}
          </select>
          <input type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="px-3 py-2 border rounded-lg text-sm w-28" style={{ borderColor: '#E0E0E0' }} data-testid="expense-amount" />
          <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }}>
            {['GBP', 'USD', 'EUR'].map(c => <option key={c}>{c}</option>)}
          </select>
          <input placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="flex-1 min-w-[160px] px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="expense-notes" />
          <button onClick={addExpense} className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5" style={{ backgroundColor: '#7B4FD8' }} data-testid="add-expense-btn"><Plus size={14} /> Add</button>
        </div>
        <div className="mt-4 pt-4" style={{ borderTop: '1px solid #F0F0F0' }}>
          <p className="text-xs font-bold text-gray-600 mb-1">Bulk import (CSV) — for earlier projects & Emergent credit spend</p>
          <p className="text-[11px] text-gray-400 mb-2">One row per expense: date,category,amount,currency,notes — e.g. 2025-11-02,Emergent credits,42.50,USD,Job runs</p>
          <textarea value={csvText} onChange={e => setCsvText(e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg text-xs font-mono" style={{ borderColor: '#E0E0E0' }} placeholder="2025-11-02,Emergent credits,42.50,USD,Job runs" data-testid="csv-import-textarea" />
          <button onClick={importCsv} disabled={busy || !csvText.trim()} className="mt-2 px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 disabled:opacity-50" style={{ backgroundColor: '#F5831F' }} data-testid="csv-import-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg overflow-x-auto" style={{ border: '1px solid #E0E0E0' }}>
        <table className="w-full text-sm">
          <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
            {['Date', 'Category', 'Amount', 'Notes', ''].map(hh => <th key={hh} className="text-left p-3 text-xs font-bold" style={{ color: '#374151' }}>{hh}</th>)}
          </tr></thead>
          <tbody>
            {(data?.expenses || []).slice(0, 100).map(e => (
              <tr key={e.expense_id} style={{ borderTop: '1px solid #F0F0F0' }} data-testid={`expense-row-${e.expense_id}`}>
                <td className="p-3 whitespace-nowrap">{e.date}</td>
                <td className="p-3">{e.category}</td>
                <td className="p-3 font-semibold">{e.currency === 'USD' ? '$' : e.currency === 'EUR' ? '€' : '£'}{Number(e.amount).toFixed(2)}</td>
                <td className="p-3 text-gray-500 max-w-[300px] truncate">{e.notes}</td>
                <td className="p-3"><button onClick={() => removeExpense(e.expense_id)} className="text-red-400 hover:text-red-600" data-testid={`delete-expense-${e.expense_id}`}><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {(!data || data.expenses.length === 0) && <tr><td colSpan={5} className="p-8 text-center text-sm text-gray-400">No expenses yet — add one above or bulk-import a CSV.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinanceExpensesPanel;
